/**
 * Logs camera models seen in production that lack an entry in the local
 * cameraImagesManifest. These rows tell us which cameras to add to the next
 * scrape iteration.
 *
 * Writes are upserted (one document per modelName) and debounced in-memory so
 * a single dashboard session does not spam Firestore for the same connected
 * camera.
 */
import { doc, setDoc, serverTimestamp, increment } from "firebase/firestore";

import { db, auth } from "@/config/firebase";

const seenInSession = new Set<string>();

const COLLECTION = "unknownCameraModels";

function buildDocId(modelName: string): string {
  return modelName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "unknown";
}

export async function logUnknownCameraModel(
  modelName: string,
  manufacturer?: string | null,
): Promise<void> {
  if (!modelName) return;
  const docId = buildDocId(`${manufacturer ?? ""}-${modelName}`);
  if (seenInSession.has(docId)) return;
  seenInSession.add(docId);

  try {
    await setDoc(
      doc(db, COLLECTION, docId),
      {
        modelName,
        manufacturer: manufacturer ?? null,
        lastSeen: serverTimestamp(),
        seenCount: increment(1),
        lastReportedBy: auth.currentUser?.uid ?? null,
      },
      { merge: true },
    );
  } catch (err) {
    // Silently swallow: this is best-effort telemetry and must not break UI.
    console.warn("[unknownCameraService] failed to log model", modelName, err);
  }
}
